import { useEffect, useId, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { IconCalendar, IconChevronLeft, IconChevronRight, IconClose } from '@/components/Icons';
import type { DateRange } from '@/features/portal/filters';
import { addDays, addMonths, endOfMonth, startOfMonth } from '@/features/portal/filters';
import { formatPosDate, formatPosMonth } from '@/lib/format';

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const NARROW = '(max-width: 639px)';

function useNarrow(): boolean {
  const [narrow, setNarrow] = useState(() => window.matchMedia(NARROW).matches);
  useEffect(() => {
    const query = window.matchMedia(NARROW);
    const onChange = () => setNarrow(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return narrow;
}

/** "2026-10-27" → "27/10/2026", the same look the date inputs had. */
const dmy = (day: string) => `${day.slice(8, 10)}/${day.slice(5, 7)}/${day.slice(0, 4)}`;

/** Monday-first weeks of one month; null pads the first and last week. */
function monthGrid(month: string): (string | null)[] {
  const first = startOfMonth(month);
  const lead = (new Date(`${first}T00:00:00Z`).getUTCDay() + 6) % 7;
  const days = Number(endOfMonth(month).slice(8, 10));
  const cells: (string | null)[] = Array.from({ length: lead }, () => null);
  for (let d = 0; d < days; d++) cells.push(addDays(first, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

/**
 * The From/To dates as one box. It opens one calendar: the first click is the
 * start, the second the end (an earlier day swaps them; the same day twice is
 * one day), and it closes itself.
 */
export function DateRangeField({ value, today, onChange }: { value: DateRange; today: string; onChange: (range: DateRange) => void }) {
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState<string | null>(null);
  const [hover, setHover] = useState<string | null>(null);
  const [month, setMonth] = useState(() => startOfMonth(value.to));
  const narrow = useNarrow();
  const root = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const panelId = useId();

  const close = () => {
    setOpen(false);
    setStart(null);
    setHover(null);
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!root.current?.contains(target) && !panel.current?.contains(target)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // The page must not scroll behind the phone sheet.
  useEffect(() => {
    if (!open || !narrow) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open, narrow]);

  const toggle = () => {
    if (open) return close();
    setMonth(startOfMonth(value.to));
    setOpen(true);
  };

  const pick = (day: string) => {
    if (start === null) {
      setStart(day);
      return;
    }
    onChange(start <= day ? { from: start, to: day } : { from: day, to: start });
    close();
  };

  // Phones: a full-width sheet from the bottom, through a portal because the
  // sticky filter bar's backdrop blur would trap a fixed element inside it.
  const sheetOnPhone = (panelEl: ReactElement) =>
    narrow
      ? createPortal(
          <div className="fixed inset-0 z-50 flex flex-col justify-end">
            <div className="absolute inset-0 bg-slate-900/40" aria-hidden="true" onClick={close} />
            {panelEl}
          </div>,
          document.body,
        )
      : panelEl;

  // Shaded range: the current one, or while choosing, from the start to the pointer.
  const end = start === null ? null : (hover ?? start);
  const low = start === null ? value.from : start < end! ? start : end!;
  const high = start === null ? value.to : start < end! ? end! : start;

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={panelId}
        aria-haspopup="dialog"
        aria-label={`Dates: ${formatPosDate(value.from)} to ${formatPosDate(value.to)}`}
        className="input flex h-9 items-center gap-2 px-3 py-1 text-xs tabular-nums"
      >
        <IconCalendar className="h-4 w-4 shrink-0 text-slate-400" />
        <span className="whitespace-nowrap text-slate-800">
          {dmy(value.from)} <span className="text-slate-400">→</span> {dmy(value.to)}
        </span>
      </button>

      {open ? sheetOnPhone(
        <div
          ref={panel}
          id={panelId}
          role="dialog"
          aria-modal={narrow || undefined}
          aria-label="Choose dates"
          className={
            narrow
              ? 'relative max-h-[90vh] w-full overflow-y-auto rounded-t-2xl bg-white p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-2xl'
              : 'absolute left-0 top-full z-30 mt-2 w-[19rem] rounded-xl border border-slate-200 bg-white p-3 shadow-xl'
          }
        >
          {narrow ? (
            <div className="mb-3 flex items-center justify-between border-b border-slate-100 pb-3">
              <p className="text-base font-semibold text-slate-900">Choose dates</p>
              <button type="button" onClick={close} className="btn btn-sm btn-ghost h-10 px-2" aria-label="Close">
                <IconClose className="h-5 w-5" />
              </button>
            </div>
          ) : null}
          <div className="mb-2 flex items-center justify-between">
            <button type="button" className="btn btn-sm btn-ghost h-9 px-2" onClick={() => setMonth(addMonths(month, -1))} aria-label="Previous month">
              <IconChevronLeft className="h-4 w-4" />
            </button>
            <p className="text-sm font-semibold text-slate-900">{formatPosMonth(month.slice(0, 7))}</p>
            <button
              type="button"
              className="btn btn-sm btn-ghost h-9 px-2"
              disabled={month >= startOfMonth(today)}
              onClick={() => setMonth(addMonths(month, 1))}
              aria-label="Next month"
            >
              <IconChevronRight className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-7 text-center text-[11px] font-medium text-slate-400">
            {WEEKDAYS.map((d) => (
              <span key={d} className="py-1">
                {d}
              </span>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-y-1" onMouseLeave={() => setHover(null)}>
            {monthGrid(month).map((day, i) => {
              if (day === null) return <span key={`pad-${i}`} />;
              const future = day > today;
              const edge = day === low || day === high;
              const inside = day > low && day < high;
              return (
                <button
                  key={day}
                  type="button"
                  disabled={future}
                  onClick={() => pick(day)}
                  onMouseEnter={() => setHover(day)}
                  aria-pressed={edge || inside}
                  aria-label={formatPosDate(day)}
                  className={`${narrow ? 'h-11 text-base' : 'h-9 text-sm'} tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 ${
                    edge
                      ? 'rounded-md bg-brand-700 font-semibold text-white'
                      : inside
                        ? 'bg-brand-600/10 text-brand-800'
                        : future
                          ? 'cursor-not-allowed text-slate-300'
                          : 'rounded-md text-slate-700 hover:bg-slate-100'
                  } ${day === today && !edge ? 'font-semibold underline decoration-brand-600 underline-offset-4' : ''}`}
                >
                  {Number(day.slice(8, 10))}
                </button>
              );
            })}
          </div>

          <p className="mt-3 rounded-md bg-slate-50 px-2 py-1.5 text-center text-xs text-slate-600" aria-live="polite">
            {start === null ? (
              <>
                <span className="font-semibold text-brand-700">Step 1:</span> click the start day
              </>
            ) : (
              <>
                Start <span className="font-semibold text-slate-900">{formatPosDate(start)}</span> ·{' '}
                <span className="font-semibold text-brand-700">Step 2:</span> click the end day
              </>
            )}
          </p>
          <p className="mt-1 text-center text-[11px] text-slate-400">Same day twice = one day</p>
        </div>,
      ) : null}
    </div>
  );
}
